import { cn } from '@veraclins-dev/utils';

import { type CustomComponent, type OverrideComponentProps } from '../types';

import { type BoxVariants, boxVariants } from './utils/variants/box';
import { extractStyleProps } from './utils/variants/styles';

export type ContainerElement =
  | 'div'
  | 'section'
  | 'article'
  | 'aside'
  | 'header'
  | 'footer'
  | 'main'
  | 'nav'
  | 'span'
  | 'figure'
  | 'figcaption'
  | 'mark'
  | 'time'
  | 'address'
  | 'details'
  | 'summary'
  | 'dialog'
  | 'fieldset'
  | 'legend'
  | 'blockquote'
  | 'pre'
  | 'output';

interface InternalBoxProps
  extends React.HTMLAttributes<HTMLElement>, BoxVariants {}

type BoxProps<P extends ContainerElement | CustomComponent = 'div'> =
  OverrideComponentProps<P, InternalBoxProps>;

/**
 * A flexible container component that exposes spacing, layout, flexbox, and grid as props.
 * This enables predictable styling of elements with a consistent API.
 * It supports both short-form and long-form spacing props, allowing for flexible usage.
 * @param {BoxProps} props - The props for the Box component.
 * @returns {JSX.Element} A rendered HTML element with applied styles and children.
 * @example
 * ```tsx
 * <Box component="section" margin={16} padding={8} display="flex" flexDirection="column">
 *   <Box component="span" padding={4}>Hello, World!</Box>
 * </Box>
 * ```
 * @category layouts
 */
function Box<P extends ContainerElement | CustomComponent = 'div'>({
  component,
  className,
  ...props
}: BoxProps<P>) {
  const Component = (component ?? 'div') as React.ElementType;

  const { styleProps, others } = extractStyleProps(props);
  return (
    <Component
      data-slot="box"
      className={cn(boxVariants({ ...styleProps, className }))}
      {...others}
    />
  );
}

/**
 * A flexible container component that exposes spacing, layout, flexbox, and grid as props.
 * This enables predictable styling of elements with a consistent API.
 * It supports both short-form and long-form spacing props, allowing for flexible usage.
 * @param {BoxProps} props - The props for the Box component.
 * @returns {JSX.Element} A rendered HTML element with applied styles and children.
 * @example
 * ```tsx
 * <Box component="section" margin={16} padding={8} display="flex" flexDirection="column">
 *   <Box component="span" padding={4}>Hello, World!</Box>
 * </Box>
 * ```
 */
// const Box = memo(Base);

export { Box, type BoxProps };
export default Box;
